import { Center, ScrollArea, Stack, Text } from '@mantine/core';
import { type FC } from 'react';
import { useNavigate } from 'react-router';
import { getOrderPath, OrderIdLink } from '~/entities/order';
import { formatOrder, type PayoutOrder } from '~/entities/payout';
import { CopyIcon } from '~/shared/ui/CopyIcon';
import { Table, type TableCell, type TableData, type TableRow } from '~/shared/ui/Table';
import { createOrderStatusBadge } from '../OrderDetails';

enum Values {
    ID = 'ID',
    SPLIT_PART = 'Часть',
    AMOUNT = 'Сумма',
    STATUS = 'Статус',
    CREATED_AT = 'Создан',
    COPY = 'Скопировать',
}

const headers: TableCell[] = [
    { key: Values.ID, content: Values.ID, ta: 'center' },
    { key: Values.SPLIT_PART, content: Values.SPLIT_PART, ta: 'center' },
    { key: Values.AMOUNT, content: Values.AMOUNT, ta: 'center' },
    { key: Values.STATUS, content: Values.STATUS, ta: 'center' },
    { key: Values.CREATED_AT, content: Values.CREATED_AT, ta: 'center' },
    { key: Values.COPY, content: Values.COPY, ta: 'center' },
];

const prepareRows = (orders: PayoutOrder[]): TableRow<PayoutOrder>[] => {
    return orders.map((order) => {
        const orderId = order.id?.value ?? '';

        return {
            key: orderId,
            item: order,
            cells: [
                {
                    key: Values.ID,
                    content: (
                        <Center>
                            <OrderIdLink
                                direction='payout'
                                orderId={orderId}
                            />
                        </Center>
                    ),
                },
                {
                    key: Values.SPLIT_PART,
                    content: <Text ta='center'>{formatOrder.splitPart(order.splitPart)}</Text>,
                },
                {
                    key: Values.AMOUNT,
                    content: (
                        <Text ta='center'>
                            {order.amount?.value} {formatOrder.currency(order.currency)}
                        </Text>
                    ),
                },
                {
                    key: Values.STATUS,
                    content: createOrderStatusBadge(order.status, true),
                },
                {
                    key: Values.CREATED_AT,
                    content: <Text ta='center'>{formatOrder.date(order.createdAt)}</Text>,
                },
                {
                    key: Values.COPY,
                    content: (
                        <Center>
                            <CopyIcon
                                stopPropagation
                                value={orderId}
                            />
                        </Center>
                    ),
                },
            ],
        };
    });
};

const prepareTableData = (orders: PayoutOrder[], isLoading: boolean): TableData<PayoutOrder> => {
    return {
        head: headers,
        rows: prepareRows(orders),
        caption: !isLoading && orders.length === 0 ? 'Дочерние ордера не найдены' : undefined,
    };
};

interface ChildOrdersProps {
    isLoading?: boolean;
    orders?: PayoutOrder[];
}

export const ChildOrders: FC<ChildOrdersProps> = ({ orders = [], isLoading = false }) => {
    const navigate = useNavigate();

    const handleRowClick = (order: PayoutOrder) => {
        const orderId = order.id?.value;

        if (!orderId) {
            return;
        }

        navigate(getOrderPath('payout', orderId));
    };

    return (
        <Stack gap={4}>
            <Text
                size='sm'
                c='dimmed'
            >
                Дочерние ордера
            </Text>

            <ScrollArea.Autosize mah={220}>
                <Table
                    striped
                    highlightOnHover={false}
                    data={prepareTableData(orders, isLoading)}
                    loading={isLoading}
                    onRowClick={handleRowClick}
                    stickyHeader={false}
                />
            </ScrollArea.Autosize>
        </Stack>
    );
};
